import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Form, Input, InputNumber, Modal, Select, Switch } from 'antd';
import dayjs from 'dayjs';

import { EMPTY_ID } from '@totem/components/common/reference/ReferenceSelector';
import AssigneeListInput from '@totem/components/ticketing/assignees/AssigneeListInput';
import DurationInput from '@totem/components/ticketing/details/tasks/durationInput';
import {
  Template,
  TemplateTask,
  TemplateTaskDefaultAssignee,
} from '@totem/components/ticketing/templateManagement/types';
import {
  Duration,
  TASK_TYPE_GROUP,
  TASK_TYPE_MILESTONE,
  TASK_TYPE_TASK,
} from '@totem/components/ticketing/ticketing';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';

const { TextArea } = Input;

export type TemplateTaskFormValues = {
  name: string;
  type: string;
  description: string;
  status: string;
  dueDate: dayjs.Dayjs | null;
  parent: number;
  budgetedHours: number;
  billingType: string;
  isBillingMilestone: boolean;
  internalOnly: boolean;
  assignees: TemplateTaskDefaultAssignee[];
};

type Props = {
  open: boolean;
  template: Template;
  task?: TemplateTask | null;
  createdByName: string;
  createdById: string;
  onCancel: () => void;
  onSave: (task: TemplateTask, isEdit: boolean) => void;
};

const defaultDuration: Duration = {
  days: 0,
  hours: 0,
  minutes: 0,
};

const TemplateTaskDialog = ({
  open,
  template,
  task,
  createdByName,
  createdById,
  onCancel,
  onSave,
}: Props) => {
  const { userProfile } = useContext(UserProfileContext);
  const [form] = Form.useForm<TemplateTaskFormValues>();
  const [duration, setDuration] = useState<Duration>(defaultDuration);

  const isEdit = isNotNull(task);

  useEffect(() => {
    if (isEdit && task) {
      setDuration(task.expectedDuration || defaultDuration);

      form.setFieldsValue({
        name: task.name,
        type: task.type,
        description: task.description,
        status: task.status,
        dueDate: task.dueDate ? dayjs(task.dueDate) : null,
        parent: task.parent,
        budgetedHours: task.budgetedHours,
        billingType: task.billingType,
        isBillingMilestone: task.isBillingMilestone,
        internalOnly: task.internalOnly,
        assignees: task.assignees || [],
      });
    } else {
      setDuration(defaultDuration);

      form.resetFields();

      form.setFieldsValue({
        type: TASK_TYPE_TASK,
        status: 'PENDING',
        parent: 0,
        budgetedHours: 0,
        isBillingMilestone: false,
        internalOnly: false,
        assignees: [],
      });
    }
  }, [form, isEdit, task, open]);

  const parentOptions = useMemo(() => {
    const options = (template.tasks || [])
      .filter((existing) => (task ? existing.id !== task.id : true))
      .map((existing) => ({
        label: `${existing.number}: ${existing.name}`,
        value: existing.number,
      }));

    return [{ label: 'No Parent', value: 0 }, ...options];
  }, [template.tasks, task]);

  const handleSave = async () => {
    const values = await form.validateFields();
    const editTask = task;
    if (isEdit && !editTask) {
      return;
    }
    const now = dayjs().toISOString();
    const nextNumber = isEdit
      ? editTask.number
      : template.tasks.reduce((max, current) => {
          if (current.number > max) {
            return current.number;
          }
          return max;
        }, 0) + 1;

    const nextPosition =
      template.tasks
        .filter((existing) => existing.parent === values.parent)
        .reduce((max, current) => {
          if (current.position > max) {
            return current.position;
          }
          return max;
        }, -1) + 1;

    const resolvedPosition =
      isEdit && editTask.parent === values.parent
        ? editTask.position
        : nextPosition;

    const payload: TemplateTask = {
      id: isEdit ? editTask.id : EMPTY_ID,
      type: values.type,
      number: nextNumber,
      parent: values.parent,
      position: resolvedPosition,
      dependencies: isEdit ? editTask.dependencies : [],
      name: values.name,
      description: values.description,
      labels: isEdit ? editTask.labels : [],
      expectedDuration: duration,
      dueDate: values.dueDate ? values.dueDate.toISOString() : null,
      assignedResources: isEdit ? editTask.assignedResources : [],
      status: values.status,
      budgetedHours: values.budgetedHours,
      billingType: values.billingType || '',
      isBillingMilestone: values.isBillingMilestone || false,
      internalOnly: values.internalOnly || false,
      assignees: (values.assignees || []).map((assignee) => ({
        type: assignee.type,
        assignee: (assignee.assignee ?? '').trim(),
      })),
      createdByType: 'USER',
      createdByName: isEdit ? editTask.createdByName : createdByName,
      createdById: isEdit ? editTask.createdById : createdById,
      createdAt: isEdit ? editTask.createdAt : now,
    };

    onSave(payload, isEdit);
  };

  return (
    <Modal
      title={isEdit ? 'Edit Template Task' : 'New Template Task'}
      open={open}
      onCancel={onCancel}
      onOk={handleSave}
      okText={isEdit ? 'Save' : 'Create'}
    >
      <Form layout="vertical" form={form}>
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: 'Task name is required.' }]}
        >
          <Input placeholder="Task name" />
        </Form.Item>
        <Form.Item label="Type" name="type">
          <Select
            options={[
              { label: TASK_TYPE_TASK, value: TASK_TYPE_TASK },
              { label: TASK_TYPE_GROUP, value: TASK_TYPE_GROUP },
              { label: TASK_TYPE_MILESTONE, value: TASK_TYPE_MILESTONE },
            ]}
          />
        </Form.Item>
        <Form.Item label="Parent" name="parent">
          <Select options={parentOptions} />
        </Form.Item>
        <Form.Item label="Expected Duration">
          <DurationInput value={duration} onChange={setDuration} />
        </Form.Item>
        <Form.Item label="Budgeted Hours" name="budgetedHours">
          <InputNumber min={0} precision={2} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label="Billing Type" name="billingType">
          <Input placeholder="Billing type" />
        </Form.Item>
        <Form.Item
          label="Billing Milestone"
          name="isBillingMilestone"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        {permissions.internal.hasPermissions(userProfile) && (
          <Form.Item
            label="Internal Only"
            name="internalOnly"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        )}
        <Form.Item label="Description" name="description">
          <TextArea rows={3} placeholder="Task description" />
        </Form.Item>
        <Form.Item label="Assignees">
          <AssigneeListInput
            name="assignees"
            valueField="assignee"
            unscopedReason="Task templates can be applied to tickets for any organization"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default TemplateTaskDialog;
